import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Icon } from '../Icon';
import { EposCashflowTable } from '../EposCashflowTable';
import { colors, spacing, borderRadius, fontWeights } from '../../theme';

interface M07EposViewProps {
  cashflowHigh: any[];
  cashflowLow: any[];
  totalPremiumNum: number;
  sumAssured: string;
  formatNumber: (num: number) => string;
  parseNumber: (str: string) => number;
}

export const M07EposView: React.FC<M07EposViewProps> = ({
  cashflowHigh,
  cashflowLow,
  totalPremiumNum,
  sumAssured,
  formatNumber,
  parseNumber,
}) => {
  const [activeChartTab, setActiveChartTab] = useState<'chart' | 'table'>('table');

  const renderSvgLineChart = () => {
    const chartW = 340;
    const chartH = 140;
    const padL = 40;
    const padT = 20;

    const premiumY20 = totalPremiumNum * 20;
    const accountY20 = cashflowHigh[19]?.accountValue || 1;
    const maxVal = Math.max(premiumY20, accountY20) || 1;

    const premiumPoints = cashflowHigh
      .map((r, i) => {
        const x = padL + (i * chartW) / 19;
        const y = padT + chartH - ((totalPremiumNum * r.year) / maxVal) * chartH;
        return `${x},${y}`;
      })
      .join(' ');

    const accountPoints = cashflowHigh
      .map((r, i) => {
        const x = padL + (i * chartW) / 19;
        const y = padT + chartH - (r.accountValue / maxVal) * chartH;
        return `${x},${y}`;
      })
      .join(' ');

    const accountLowPoints = cashflowLow
      .map((r, i) => {
        const x = padL + (i * chartW) / 19;
        const y = padT + chartH - (r.accountValue / maxVal) * chartH;
        return `${x},${y}`;
      })
      .join(' ');

    return (
      <View style={styles.chartCard}>
        <View style={styles.chartHeader}>
          <Text style={styles.chartTitle}>Dự báo Dòng tiền (20 Năm)</Text>
          <View style={styles.chartLegends}>
            <View style={styles.legendItem}>
              <View style={[styles.legendDot, { backgroundColor: colors.primary }]} />
              <Text style={styles.legendText}>TỔNG PHÍ ĐÓNG</Text>
            </View>
            <View style={styles.legendItem}>
              <View style={[styles.legendDot, { backgroundColor: colors.secondary }]} />
              <Text style={styles.legendText}>DỰ KIẾN (4.0%)</Text>
            </View>
            <View style={styles.legendItem}>
              <View style={[styles.legendDot, { backgroundColor: '#FF6B00', borderRadius: 0 }]} />
              <Text style={styles.legendText}>CAM KẾT (0.5%)</Text>
            </View>
          </View>
        </View>

        {Platform.OS === 'web' ? (
          <View style={styles.svgChartContainer}>
            <svg width="100%" height="180" viewBox="0 0 400 180" preserveAspectRatio="none">
              <line x1="40" y1="20" x2="380" y2="20" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4 4" />
              <line x1="40" y1="55" x2="380" y2="55" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4 4" />
              <line x1="40" y1="90" x2="380" y2="90" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4 4" />
              <line x1="40" y1="125" x2="380" y2="125" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4 4" />
              <line x1="40" y1="160" x2="380" y2="160" stroke="#cbd5e1" strokeWidth="1" />
              <line x1="40" y1="10" x2="40" y2="160" stroke="#cbd5e1" strokeWidth="1" />
              <polyline fill="none" points={premiumPoints} stroke={colors.primary} strokeWidth="2.5" />
              <polyline fill="none" points={accountPoints} stroke={colors.secondary} strokeWidth="3" />
              <polyline fill="none" points={accountLowPoints} stroke="#FF6B00" strokeWidth="2" strokeDasharray="3 3" />
            </svg>

            <View style={styles.xAxisRow}>
              <Text style={styles.xAxisLabel}>Năm 1</Text>
              <Text style={styles.xAxisLabel}>Năm 5</Text>
              <Text style={styles.xAxisLabel}>Năm 10</Text>
              <Text style={styles.xAxisLabel}>Năm 15</Text>
              <Text style={styles.xAxisLabel}>Năm 20</Text>
            </View>
          </View>
        ) : (
          <View style={styles.fallbackChartBox}>
            <Text style={styles.fallbackChartText}>[Biểu đồ dòng tiền eQuote - Dạng đồ thị đường]</Text>
          </View>
        )}
      </View>
    );
  };

  return (
    <View style={styles.eposContainer}>
      <View style={styles.eposTabBar}>
        <View style={styles.eposTabLeftRow}>
          <TouchableOpacity
            style={[styles.eposTabItem, activeChartTab === 'chart' && styles.eposTabItemActive]}
            onPress={() => setActiveChartTab('chart')}
          >
            <Text style={[styles.eposTabText, activeChartTab === 'chart' && styles.eposTabTextActive]}>
              Biểu đồ
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.eposTabItem, activeChartTab === 'table' && styles.eposTabItemActive]}
            onPress={() => setActiveChartTab('table')}
          >
            <Text style={[styles.eposTabText, activeChartTab === 'table' && styles.eposTabTextActive]}>
              Bảng
            </Text>
          </TouchableOpacity>
        </View>

        <View style={styles.eposTabRightControls}>
          <TouchableOpacity style={styles.eposControlPill}>
            <Icon name="settings" size={14} color="#475569" />
            <Text style={styles.eposControlText}>Lựa Chọn Hiển Thị Bảng</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.eposControlPill}>
            <Icon name="trending_up" size={14} color="#475569" />
            <Text style={styles.eposControlText}>Biến động Quỹ</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.infoIconBtn}>
            <Icon name="info" size={16} color="#64748b" />
          </TouchableOpacity>

          <Text style={styles.unitText}>Đơn vị: VNĐ</Text>
        </View>
      </View>

      {activeChartTab === 'chart' ? (
        renderSvgLineChart()
      ) : (
        <EposCashflowTable
          cashflowHigh={cashflowHigh}
          cashflowLow={cashflowLow}
          sumAssured={sumAssured}
          formatNumber={formatNumber}
          parseNumber={parseNumber}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  eposContainer: {
    gap: spacing[4],
  },
  eposTabBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#ffffff',
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[2.5],
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    flexWrap: 'wrap',
    gap: spacing[2],
  },
  eposTabLeftRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    borderRadius: borderRadius.sm,
    padding: 3,
  },
  eposTabItem: {
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[1.5],
    borderRadius: borderRadius.sm - 1,
  },
  eposTabItemActive: {
    backgroundColor: '#ffffff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
  eposTabText: {
    fontSize: 13,
    fontWeight: fontWeights.medium,
    color: '#64748b',
  },
  eposTabTextActive: {
    color: colors.primary,
    fontWeight: fontWeights.bold,
  },
  eposTabRightControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2.5],
    flexWrap: 'wrap',
  },
  eposControlPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1.5],
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[1.5],
  },
  eposControlText: {
    fontSize: 12,
    fontWeight: fontWeights.semibold,
    color: '#334155',
  },
  infoIconBtn: {
    padding: spacing[1],
  },
  unitText: {
    fontSize: 12,
    fontStyle: 'italic',
    color: '#64748b',
    marginLeft: spacing[1],
  },
  chartCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: '#e2e8f0',
    gap: spacing[3],
  },
  chartHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing[2],
  },
  chartTitle: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: '#1e293b',
  },
  chartLegends: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1.5],
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  legendText: {
    fontSize: 11,
    fontWeight: fontWeights.semibold,
    color: '#475569',
  },
  svgChartContainer: {
    width: '100%',
    alignItems: 'center',
  },
  xAxisRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    paddingLeft: 40,
    paddingRight: 20,
    marginTop: spacing[2],
  },
  xAxisLabel: {
    fontSize: 11,
    color: '#64748b',
    fontWeight: fontWeights.medium,
  },
  fallbackChartBox: {
    height: 180,
    backgroundColor: '#f8fafc',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: borderRadius.sm,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  fallbackChartText: {
    fontSize: 13,
    color: '#64748b',
    fontStyle: 'italic',
  },
});
